import {
  Alert,
  Box,
  Button,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCalculator,
  IconPlus,
  IconRefresh,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";
import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getToolBySlug,
  getToolsBySlugs,
  ToolCatalogItem,
} from "../../data/toolsCatalog";
import {
  ToolPageLayout,
  ToolPageLayoutProps,
  trackToolEvent,
} from "./shared/ToolPageLayout";
import "./StudentUtilityToolPage.css";

type NumberValue = number | "";
type ToolSlug =
  | "semester-grade-calculator"
  | "grade-calculator"
  | "midterm-grade-calculator"
  | "final-grade-calculator"
  | "letter-grade-calculator"
  | "cumulative-gpa-calculator"
  | "ai-college-essay-reviewer"
  | "college-essay-editor"
  | "speech-word-counter"
  | "speech-time-calculator";

type ToolContent = Pick<
  ToolPageLayoutProps,
  | "description"
  | "howToUseSteps"
  | "exampleContent"
  | "formulaSection"
  | "whySection"
  | "commonMistakes"
  | "faqItems"
  | "ctaTitle"
  | "ctaDescription"
  | "ctaButtonText"
  | "ctaButtonHref"
>;

type GradeRow = {
  id: string;
  name: string;
  score: NumberValue;
  total: NumberValue;
  percent: NumberValue;
  weight: NumberValue;
};

type GpaRow = {
  id: string;
  name: string;
  gpa: NumberValue;
  credits: NumberValue;
};

const supportedToolSlugs: ToolSlug[] = [
  "semester-grade-calculator",
  "midterm-grade-calculator",
  "letter-grade-calculator",
  "cumulative-gpa-calculator",
  "ai-college-essay-reviewer",
  "college-essay-editor",
  "speech-word-counter",
];

const defaultGradeRows: GradeRow[] = [
  { id: "assignment", name: "Assignment", score: 42, total: 50, percent: 84, weight: 30 },
  { id: "quiz", name: "Quiz", score: 18, total: 20, percent: 90, weight: 10 },
  { id: "exam", name: "Exam", score: 76, total: 100, percent: 76, weight: 40 },
  { id: "project", name: "Project", score: 45, total: 50, percent: 90, weight: 20 },
];

const defaultGpaRows: GpaRow[] = [
  { id: "fall", name: "Fall Semester", gpa: 3.4, credits: 15 },
  { id: "spring", name: "Spring Semester", gpa: 3.6, credits: 15 },
];

const speechSpeeds = {
  slow: 100,
  average: 130,
  fast: 160,
};

function StudentUtilityToolPage({ slug }: { slug: ToolSlug }) {
  const tool = getToolBySlug(slug);
  const content = toolPageContent[slug];

  if (!tool || !content) {
    return (
      <Box p="xl">
        <Title order={2}>Tool not found</Title>
        <Text component={Link} to="/tools" c="blue">
          Back to tools
        </Text>
      </Box>
    );
  }

  const relatedTools = getToolsBySlugs(tool.relatedTools);

  return (
    <ToolPageLayout
      title={tool.title}
      description={content.description}
      primaryKeyword={tool.primaryKeyword}
      secondaryKeywords={tool.secondaryKeywords}
      path={tool.url}
      category={tool.category}
      seoTitle={tool.seoTitle}
      metaDescription={tool.metaDescription}
      toolComponent={renderToolComponent(slug, tool)}
      howToUseSteps={content.howToUseSteps}
      exampleContent={content.exampleContent}
      formulaSection={content.formulaSection}
      whySection={content.whySection}
      commonMistakes={content.commonMistakes}
      faqItems={content.faqItems}
      relatedTools={relatedTools}
      relatedBlogs={tool.relatedBlogs}
      relatedServices={tool.relatedServices}
      ctaTitle={content.ctaTitle}
      ctaDescription={content.ctaDescription}
      ctaButtonText={content.ctaButtonText}
      ctaButtonHref={content.ctaButtonHref}
    />
  );
}

export function getStudentUtilityToolRoutes() {
  return supportedToolSlugs;
}

function renderToolComponent(slug: ToolSlug, tool: ToolCatalogItem) {
  switch (slug) {
    case "semester-grade-calculator":
      return <SemesterGradeTool tool={tool} />;
    case "grade-calculator":
      return <GradeCalculatorTool tool={tool} />;
    case "midterm-grade-calculator":
      return <MidtermGradeTool tool={tool} />;
    case "final-grade-calculator":
      return <FinalGradeTool tool={tool} />;
    case "letter-grade-calculator":
      return <LetterGradeTool tool={tool} />;
    case "cumulative-gpa-calculator":
      return <CumulativeGpaTool tool={tool} />;
    case "ai-college-essay-reviewer":
      return <AiEssayReviewerTool tool={tool} />;
    case "college-essay-editor":
      return <CollegeEssayEditorTool tool={tool} />;
    case "speech-word-counter":
      return <SpeechWordCounterTool tool={tool} />;
    case "speech-time-calculator":
      return <SpeechTimeCalculatorTool tool={tool} />;
    default:
      return null;
  }
}

function numberValue(value: NumberValue) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function toNumberInputValue(value: string | number): NumberValue {
  return value === "" ? "" : Number(value);
}

function formatNumber(value: number, digits = 1) {
  return Number.isFinite(value) ? value.toFixed(digits) : "0.0";
}

function formatPercent(value: number) {
  return `${formatNumber(value)}%`;
}

function createId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.round(Math.random() * 1000)}`;
}

function getLetterGrade(percent: number) {
  if (percent >= 90) {
    return "A";
  }

  if (percent >= 80) {
    return "B";
  }

  if (percent >= 70) {
    return "C";
  }

  if (percent >= 60) {
    return "D";
  }

  return "F";
}

function getGpaEstimate(percent: number) {
  if (percent >= 90) {
    return "4.0";
  }

  if (percent >= 80) {
    return "3.0";
  }

  if (percent >= 70) {
    return "2.0";
  }

  if (percent >= 60) {
    return "1.0";
  }

  return "0.0";
}

function validatePercent(value: NumberValue, label: string) {
  if (value === "") {
    return `${label} is required.`;
  }

  const parsed = numberValue(value);

  if (parsed < 0 || parsed > 100) {
    return `${label} must be between 0 and 100.`;
  }

  return "";
}

function validateNonNegative(value: NumberValue, label: string) {
  if (value === "") {
    return `${label} is required.`;
  }

  if (numberValue(value) < 0) {
    return `${label} cannot be negative.`;
  }

  return "";
}

function trackCalculation(tool: ToolCatalogItem, resultType: string) {
  trackToolEvent("tool_calculate", {
    tool_name: tool.title,
    tool_category: tool.category,
    primary_keyword: tool.primaryKeyword,
    result_type: resultType,
  });
  trackToolEvent("tool_result_generated", {
    tool_name: tool.title,
    tool_category: tool.category,
    primary_keyword: tool.primaryKeyword,
    result_type: resultType,
  });
}

function ResultStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Paper withBorder radius="md" p="md" className="utility-stat">
      <Text fw={900} size="xl">
        {value}
      </Text>
      <Text size="sm" c="dimmed">
        {label}
      </Text>
    </Paper>
  );
}

function ValidationAlerts({ errors }: { errors: string[] }) {
  if (!errors.length) {
    return null;
  }

  return (
    <Alert icon={<IconAlertCircle size={18} />} color="red" radius="md">
      <ul className="utility-alert-list">
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </Alert>
  );
}

function WeightWarning({ totalWeight }: { totalWeight: number }) {
  if (Math.abs(totalWeight - 100) <= 0.01) {
    return (
      <Alert color="green" radius="md" variant="light">
        Your total weight is 100%, so the weighted result is based on a full
        grading structure.
      </Alert>
    );
  }

  return (
    <Alert color="yellow" radius="md" variant="light">
      Your total weight is {formatPercent(totalWeight)}. If your grading policy
      is standard, weights should add up to 100%.
    </Alert>
  );
}

function SemesterGradeTool({ tool }: { tool: ToolCatalogItem }) {
  const [mode, setMode] = useState<"calculate" | "required-final">("calculate");
  const [firstQuarterGrade, setFirstQuarterGrade] = useState<NumberValue>(84);
  const [firstQuarterWeight, setFirstQuarterWeight] = useState<NumberValue>(40);
  const [secondQuarterGrade, setSecondQuarterGrade] = useState<NumberValue>(88);
  const [secondQuarterWeight, setSecondQuarterWeight] = useState<NumberValue>(40);
  const [finalExamGrade, setFinalExamGrade] = useState<NumberValue>(90);
  const [finalExamWeight, setFinalExamWeight] = useState<NumberValue>(20);
  const [targetSemesterGrade, setTargetSemesterGrade] = useState<NumberValue>(85);
  const [touched, setTouched] = useState(false);

  const result = useMemo(() => {
    const errors = [
      validatePercent(firstQuarterGrade, "First quarter grade"),
      validatePercent(firstQuarterWeight, "First quarter weight"),
      validatePercent(secondQuarterGrade, "Second quarter grade"),
      validatePercent(secondQuarterWeight, "Second quarter weight"),
      validatePercent(finalExamWeight, "Final exam weight"),
      mode === "calculate"
        ? validatePercent(finalExamGrade, "Final exam grade")
        : validatePercent(targetSemesterGrade, "Target semester grade"),
    ].filter(Boolean);

    const totalWeight =
      numberValue(firstQuarterWeight) +
      numberValue(secondQuarterWeight) +
      numberValue(finalExamWeight);
    const weightError =
      Math.abs(totalWeight - 100) > 0.01
        ? "Total weights must equal 100% before this semester grade estimate is reliable."
        : "";
    const currentWeightedGrade =
      numberValue(firstQuarterGrade) * (numberValue(firstQuarterWeight) / 100) +
      numberValue(secondQuarterGrade) * (numberValue(secondQuarterWeight) / 100);
    const semesterGrade =
      currentWeightedGrade +
      numberValue(finalExamGrade) * (numberValue(finalExamWeight) / 100);
    const requiredFinalExamGrade =
      numberValue(finalExamWeight) > 0
        ? (numberValue(targetSemesterGrade) - currentWeightedGrade) /
          (numberValue(finalExamWeight) / 100)
        : Number.POSITIVE_INFINITY;
    const finalWeightError =
      mode === "required-final" && numberValue(finalExamWeight) <= 0
        ? "Final exam weight must be greater than 0% to calculate the required final exam grade."
        : "";

    return {
      errors: [...errors, weightError, finalWeightError].filter(Boolean),
      totalWeight,
      currentWeightedGrade,
      semesterGrade,
      semesterLetter: getLetterGrade(semesterGrade),
      requiredFinalExamGrade,
      targetLetter: getLetterGrade(numberValue(targetSemesterGrade)),
    };
  }, [
    finalExamGrade,
    finalExamWeight,
    firstQuarterGrade,
    firstQuarterWeight,
    mode,
    secondQuarterGrade,
    secondQuarterWeight,
    targetSemesterGrade,
  ]);

  function reset() {
    setMode("calculate");
    setFirstQuarterGrade(84);
    setFirstQuarterWeight(40);
    setSecondQuarterGrade(88);
    setSecondQuarterWeight(40);
    setFinalExamGrade(90);
    setFinalExamWeight(20);
    setTargetSemesterGrade(85);
    setTouched(false);
  }

  const requiredFinalNote =
    result.requiredFinalExamGrade > 100
      ? "The required final exam grade is above 100%, so the target may not be reachable with these weights."
      : result.requiredFinalExamGrade < 0
        ? "Your quarter grades already meet the target before the final exam contribution is added."
        : "Use this required final exam grade as a planning estimate and compare it with your school's official grading policy.";

  return (
    <Stack gap="lg">
      <ToolInputsTitle
        title="Semester Grade Calculator"
        description="Choose whether to calculate your final semester grade or find what grade you need on your final exam."
      />

      <SegmentedControl
        fullWidth
        value={mode}
        onChange={(value) => {
          setMode(value as "calculate" | "required-final");
          setTouched(false);
        }}
        data={[
          { label: "Calculate semester grade", value: "calculate" },
          { label: "What grade do I need on my final?", value: "required-final" },
        ]}
      />

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <PercentNumberInput
          label="First quarter grade"
          value={firstQuarterGrade}
          onChange={setFirstQuarterGrade}
        />
        <PercentNumberInput
          label="First quarter weight"
          value={firstQuarterWeight}
          onChange={setFirstQuarterWeight}
        />
        <PercentNumberInput
          label="Second quarter grade"
          value={secondQuarterGrade}
          onChange={setSecondQuarterGrade}
        />
        <PercentNumberInput
          label="Second quarter weight"
          value={secondQuarterWeight}
          onChange={setSecondQuarterWeight}
        />
        {mode === "calculate" ? (
          <PercentNumberInput
            label="Final exam grade"
            value={finalExamGrade}
            onChange={setFinalExamGrade}
          />
        ) : (
          <PercentNumberInput
            label="Target semester grade"
            value={targetSemesterGrade}
            onChange={setTargetSemesterGrade}
          />
        )}
        <PercentNumberInput
          label="Final exam weight"
          value={finalExamWeight}
          onChange={setFinalExamWeight}
        />
      </SimpleGrid>

      <CalculateControls
        calculateLabel={
          mode === "calculate"
            ? "Calculate Semester Grade"
            : "Calculate Required Final Grade"
        }
        onCalculate={() => {
          setTouched(true);
          if (!result.errors.length) {
            trackCalculation(
              tool,
              mode === "calculate" ? "semester_grade" : "required_final_grade",
            );
          }
        }}
        onReset={reset}
      />

      {touched ? <ValidationAlerts errors={result.errors} /> : null}
      <WeightWarning totalWeight={result.totalWeight} />

      {!result.errors.length ? (
        mode === "calculate" ? (
          <ResultSummary
            stats={[
              ["Semester grade", formatPercent(result.semesterGrade)],
              ["Estimated letter grade", result.semesterLetter],
              ["Total weight", formatPercent(result.totalWeight)],
            ]}
            note={
              <>
                Semester Grade = (First Quarter Grade x First Quarter Weight /
                100) + (Second Quarter Grade x Second Quarter Weight / 100) +
                (Final Exam Grade x Final Exam Weight / 100). This is an
                estimate; official grade scales and rounding rules vary by
                school.
              </>
            }
          />
        ) : (
          <ResultSummary
            stats={[
              [
                "Required final exam grade",
                Number.isFinite(result.requiredFinalExamGrade)
                  ? formatPercent(result.requiredFinalExamGrade)
                  : "Check final weight",
              ],
              ["Target letter estimate", result.targetLetter],
              ["Current weighted grade", formatPercent(result.currentWeightedGrade)],
            ]}
            note={
              <>
                Required Final Exam Grade = (Target Semester Grade - Current
                Weighted Grade) / (Final Exam Weight / 100). {requiredFinalNote}{" "}
                Official grade scales and pass thresholds vary by school.
              </>
            }
          />
        )
      ) : null}
    </Stack>
  );
}

function GradeCalculatorTool({ tool }: { tool: ToolCatalogItem }) {
  const [rows, setRows] = useState<GradeRow[]>(defaultGradeRows);
  const [percentageMode, setPercentageMode] = useState(false);
  const [touched, setTouched] = useState(false);

  const result = useMemo(() => {
    const errors = rows.flatMap((row) => {
      const rowErrors = [
        row.name.trim() ? "" : "Assessment name is required.",
        validateNonNegative(row.weight, `${row.name || "Assessment"} weight`),
      ];

      if (numberValue(row.weight) > 100) {
        rowErrors.push(`${row.name || "Assessment"} weight cannot exceed 100%.`);
      }

      if (percentageMode) {
        rowErrors.push(validatePercent(row.percent, `${row.name || "Assessment"} percentage`));
      } else {
        rowErrors.push(validateNonNegative(row.score, `${row.name || "Assessment"} score`));
        if (row.total === "" || numberValue(row.total) <= 0) {
          rowErrors.push(`${row.name || "Assessment"} total score must be greater than 0.`);
        }
      }

      return rowErrors.filter(Boolean);
    });
    const totalWeight = rows.reduce((sum, row) => sum + numberValue(row.weight), 0);
    const totalGrade = rows.reduce((sum, row) => {
      const percent = percentageMode
        ? numberValue(row.percent)
        : numberValue(row.total) > 0
          ? (numberValue(row.score) / numberValue(row.total)) * 100
          : 0;
      return sum + percent * (numberValue(row.weight) / 100);
    }, 0);

    return {
      errors,
      totalWeight,
      totalGrade,
      letter: getLetterGrade(totalGrade),
    };
  }, [percentageMode, rows]);

  function updateRow(id: string, updates: Partial<GradeRow>) {
    setRows((currentRows) =>
      currentRows.map((row) => (row.id === id ? { ...row, ...updates } : row)),
    );
  }

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={2}>Calculate Assignment and Course Grades</Title>
        <Text c="dimmed">
          Add scores, totals, and grade weights. Switch to percentage-only mode
          when you already know each assessment percentage.
        </Text>
      </Stack>

      <Switch
        checked={percentageMode}
        onChange={(event) => setPercentageMode(event.currentTarget.checked)}
        label="Use percentage-only mode"
      />

      <Stack gap="sm">
        {rows.map((row, index) => (
          <Paper key={row.id} withBorder radius="md" p="md" className="utility-row">
            <SimpleGrid cols={{ base: 1, md: percentageMode ? 4 : 5 }} spacing="sm">
              <TextInput
                label="Assessment name"
                value={row.name}
                onChange={(event) =>
                  updateRow(row.id, { name: event.currentTarget.value })
                }
              />
              {percentageMode ? (
                <NumberInput
                  label="Grade percentage"
                  value={row.percent}
                  min={0}
                  max={100}
                  rightSection={<Text size="xs">%</Text>}
                  onChange={(value) =>
                    updateRow(row.id, { percent: toNumberInputValue(value) })
                  }
                />
              ) : (
                <>
                  <NumberInput
                    label="Score received"
                    value={row.score}
                    min={0}
                    onChange={(value) =>
                      updateRow(row.id, { score: toNumberInputValue(value) })
                    }
                  />
                  <NumberInput
                    label="Total score"
                    value={row.total}
                    min={0}
                    onChange={(value) =>
                      updateRow(row.id, { total: toNumberInputValue(value) })
                    }
                  />
                </>
              )}
              <NumberInput
                label="Weight percentage"
                value={row.weight}
                min={0}
                max={100}
                rightSection={<Text size="xs">%</Text>}
                onChange={(value) =>
                  updateRow(row.id, { weight: toNumberInputValue(value) })
                }
              />
              <Button
                mt={{ base: 0, md: 24 }}
                variant="light"
                color="red"
                leftSection={<IconTrash size={16} />}
                disabled={rows.length === 1}
                onClick={() =>
                  setRows((currentRows) =>
                    currentRows.filter((candidate) => candidate.id !== row.id),
                  )
                }
              >
                Remove {index + 1}
              </Button>
            </SimpleGrid>
          </Paper>
        ))}
      </Stack>

      <Group gap="sm">
        <Button
          variant="light"
          leftSection={<IconPlus size={18} />}
          onClick={() =>
            setRows((currentRows) => [
              ...currentRows,
              {
                id: createId("grade"),
                name: "New assessment",
                score: "",
                total: "",
                percent: "",
                weight: "",
              },
            ])
          }
        >
          Add Row
        </Button>
        <Button
          variant="outline"
          color="gray"
          leftSection={<IconRefresh size={18} />}
          onClick={() => {
            setRows(defaultGradeRows);
            setTouched(false);
          }}
        >
          Reset
        </Button>
        <Button
          leftSection={<IconCalculator size={18} />}
          onClick={() => {
            setTouched(true);
            if (!result.errors.length) {
              trackCalculation(tool, "course_grade");
            }
          }}
        >
          Calculate Grade
        </Button>
      </Group>

      {touched ? <ValidationAlerts errors={result.errors} /> : null}
      <WeightWarning totalWeight={result.totalWeight} />

      {!result.errors.length ? (
        <Paper withBorder radius="md" p="lg" className="utility-result">
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <ResultStat label="Total grade" value={formatPercent(result.totalGrade)} />
              <ResultStat label="Letter grade" value={result.letter} />
              <ResultStat label="Total weight" value={formatPercent(result.totalWeight)} />
            </SimpleGrid>
            <Text c="dimmed">
              This grade calculator estimates your weighted class grade from the
              rows above. Check your syllabus before treating the result as
              official.
            </Text>
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}

function MidtermGradeTool({ tool }: { tool: ToolCatalogItem }) {
  const [currentGrade, setCurrentGrade] = useState<NumberValue>(82);
  const [completedWeight, setCompletedWeight] = useState<NumberValue>(70);
  const [midtermScore, setMidtermScore] = useState<NumberValue>(76);
  const [midtermWeight, setMidtermWeight] = useState<NumberValue>(30);
  const [touched, setTouched] = useState(false);

  const result = useMemo(() => {
    const errors = [
      validatePercent(currentGrade, "Current grade before midterm"),
      validatePercent(completedWeight, "Completed coursework weight"),
      validatePercent(midtermScore, "Midterm score"),
      validatePercent(midtermWeight, "Midterm weight"),
    ].filter(Boolean);
    const currentContribution =
      numberValue(currentGrade) * (numberValue(completedWeight) / 100);
    const midtermContribution =
      numberValue(midtermScore) * (numberValue(midtermWeight) / 100);
    const updatedGrade = currentContribution + midtermContribution;
    const impact = updatedGrade - numberValue(currentGrade);

    return {
      errors,
      totalWeight: numberValue(completedWeight) + numberValue(midtermWeight),
      updatedGrade,
      impact,
      letter: getLetterGrade(updatedGrade),
    };
  }, [completedWeight, currentGrade, midtermScore, midtermWeight]);

  return (
    <Stack gap="lg">
      <ToolInputsTitle
        title="Calculate Your Grade After a Midterm"
        description="Enter your grade before the midterm, the completed coursework weight, and the midterm score and weight."
      />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <PercentNumberInput label="Current grade before midterm" value={currentGrade} onChange={setCurrentGrade} />
        <PercentNumberInput label="Completed coursework weight" value={completedWeight} onChange={setCompletedWeight} />
        <PercentNumberInput label="Midterm score" value={midtermScore} onChange={setMidtermScore} />
        <PercentNumberInput label="Midterm weight" value={midtermWeight} onChange={setMidtermWeight} />
      </SimpleGrid>
      <CalculateControls
        calculateLabel="Calculate Midterm Impact"
        onCalculate={() => {
          setTouched(true);
          if (!result.errors.length) {
            trackCalculation(tool, "midterm_grade");
          }
        }}
        onReset={() => {
          setCurrentGrade(82);
          setCompletedWeight(70);
          setMidtermScore(76);
          setMidtermWeight(30);
          setTouched(false);
        }}
      />
      {touched ? <ValidationAlerts errors={result.errors} /> : null}
      <WeightWarning totalWeight={result.totalWeight} />
      {!result.errors.length ? (
        <ResultSummary
          stats={[
            ["Updated course grade", formatPercent(result.updatedGrade)],
            ["Midterm impact", `${result.impact >= 0 ? "+" : ""}${formatPercent(result.impact)}`],
            ["Letter grade estimate", result.letter],
          ]}
          note="If the completed coursework and midterm weights do not equal 100%, this result represents the weighted contribution for the completed parts you entered."
        />
      ) : null}
    </Stack>
  );
}

function FinalGradeTool({ tool }: { tool: ToolCatalogItem }) {
  const [currentGrade, setCurrentGrade] = useState<NumberValue>(84);
  const [desiredGrade, setDesiredGrade] = useState<NumberValue>(90);
  const [finalWeight, setFinalWeight] = useState<NumberValue>(25);
  const [touched, setTouched] = useState(false);

  const result = useMemo(() => {
    const errors = [
      validatePercent(currentGrade, "Current grade"),
      validatePercent(desiredGrade, "Desired final grade"),
      validatePercent(finalWeight, "Final exam weight"),
    ].filter(Boolean);

    if (numberValue(finalWeight) <= 0) {
      errors.push("Final exam weight must be greater than 0.");
    }

    const finalWeightDecimal = numberValue(finalWeight) / 100;
    const requiredFinal =
      finalWeightDecimal > 0
        ? (numberValue(desiredGrade) -
            numberValue(currentGrade) * (1 - finalWeightDecimal)) /
          finalWeightDecimal
        : 0;

    return {
      errors,
      requiredFinal,
      targetLetter: getLetterGrade(numberValue(desiredGrade)),
    };
  }, [currentGrade, desiredGrade, finalWeight]);

  const message =
    result.requiredFinal > 100
      ? "This target may not be possible through the final exam alone."
      : result.requiredFinal <= 0
        ? "You may have already secured this target depending on your grading policy."
        : `You need about ${formatPercent(result.requiredFinal)} on your final exam.`;

  return (
    <Stack gap="lg">
      <ToolInputsTitle
        title="Find What You Need on Your Final"
        description="Enter your current grade, desired final grade, and the final exam weight."
      />
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <PercentNumberInput label="Current grade" value={currentGrade} onChange={setCurrentGrade} />
        <PercentNumberInput label="Desired final grade" value={desiredGrade} onChange={setDesiredGrade} />
        <PercentNumberInput label="Final exam weight" value={finalWeight} onChange={setFinalWeight} />
      </SimpleGrid>
      <CalculateControls
        calculateLabel="Calculate Required Final Score"
        onCalculate={() => {
          setTouched(true);
          if (!result.errors.length) {
            trackCalculation(tool, "required_final_exam_score");
          }
        }}
        onReset={() => {
          setCurrentGrade(84);
          setDesiredGrade(90);
          setFinalWeight(25);
          setTouched(false);
        }}
      />
      {touched ? <ValidationAlerts errors={result.errors} /> : null}
      {!result.errors.length ? (
        <ResultSummary
          stats={[
            ["Required final exam score", formatPercent(result.requiredFinal)],
            ["Target grade", formatPercent(numberValue(desiredGrade))],
            ["Target letter grade", result.targetLetter],
          ]}
          note={message}
        />
      ) : null}
    </Stack>
  );
}

function LetterGradeTool({ tool }: { tool: ToolCatalogItem }) {
  const [percentage, setPercentage] = useState<NumberValue>(84);
  const [touched, setTouched] = useState(false);
  const errors = [validatePercent(percentage, "Percentage score")].filter(Boolean);
  const percent = numberValue(percentage);
  const letter = getLetterGrade(percent);
  const gpa = getGpaEstimate(percent);
  const meaning =
    letter === "A"
      ? "excellent performance"
      : letter === "B"
        ? "strong performance"
        : letter === "C"
          ? "satisfactory performance"
          : letter === "D"
            ? "passing but vulnerable performance"
            : "below a common passing threshold";

  return (
    <Stack gap="lg">
      <ToolInputsTitle
        title="Convert Percentage to Letter Grade"
        description="Enter a percentage score to see the estimated letter grade and GPA equivalent."
      />
      <PercentNumberInput label="Percentage score" value={percentage} onChange={setPercentage} />
      <CalculateControls
        calculateLabel="Calculate Letter Grade"
        onCalculate={() => {
          setTouched(true);
          if (!errors.length) {
            trackCalculation(tool, "letter_grade");
          }
        }}
        onReset={() => {
          setPercentage(84);
          setTouched(false);
        }}
      />
      {touched ? <ValidationAlerts errors={errors} /> : null}
      {!errors.length ? (
        <ResultSummary
          stats={[
            ["Letter grade", letter],
            ["GPA estimate", gpa],
            ["Grade meaning", meaning],
          ]}
          note={`A ${formatPercent(percent)} is approximately a ${letter} on a common US scale. Grade scales can vary by school, college, university, and country. Always check your institution's grading policy.`}
        />
      ) : null}
    </Stack>
  );
}

function CumulativeGpaTool({ tool }: { tool: ToolCatalogItem }) {
  const [rows, setRows] = useState<GpaRow[]>(defaultGpaRows);
  const [touched, setTouched] = useState(false);

  const result = useMemo(() => {
    const errors = rows.flatMap((row) => {
      const rowErrors = [
        row.name.trim() ? "" : "Semester name is required.",
        row.gpa === "" ? `${row.name || "Semester"} GPA is required.` : "",
        row.credits === "" ? `${row.name || "Semester"} credits are required.` : "",
      ];

      if (numberValue(row.gpa) < 0 || numberValue(row.gpa) > 4) {
        rowErrors.push(`${row.name || "Semester"} GPA must be between 0 and 4.0.`);
      }

      if (numberValue(row.credits) <= 0) {
        rowErrors.push(`${row.name || "Semester"} credits must be greater than 0.`);
      }

      return rowErrors.filter(Boolean);
    });
    const totalCredits = rows.reduce((sum, row) => sum + numberValue(row.credits), 0);
    const totalGradePoints = rows.reduce(
      (sum, row) => sum + numberValue(row.gpa) * numberValue(row.credits),
      0,
    );
    const cumulativeGpa = totalCredits > 0 ? totalGradePoints / totalCredits : 0;

    return {
      errors,
      totalCredits,
      totalGradePoints,
      cumulativeGpa,
    };
  }, [rows]);

  function updateRow(id: string, updates: Partial<GpaRow>) {
    setRows((currentRows) =>
      currentRows.map((row) => (row.id === id ? { ...row, ...updates } : row)),
    );
  }

  return (
    <Stack gap="lg">
      <ToolInputsTitle
        title="Calculate GPA Across Semesters"
        description="Add each semester GPA and its credit hours to calculate your cumulative GPA on a 4.0 scale."
      />
      <Stack gap="sm">
        {rows.map((row, index) => (
          <Paper key={row.id} withBorder radius="md" p="md" className="utility-row">
            <SimpleGrid cols={{ base: 1, md: 4 }} spacing="sm">
              <TextInput
                label="Semester name"
                value={row.name}
                onChange={(event) =>
                  updateRow(row.id, { name: event.currentTarget.value })
                }
              />
              <NumberInput
                label="Semester GPA"
                value={row.gpa}
                min={0}
                max={4}
                decimalScale={2}
                onChange={(value) =>
                  updateRow(row.id, { gpa: toNumberInputValue(value) })
                }
              />
              <NumberInput
                label="Credits or hours"
                value={row.credits}
                min={0}
                onChange={(value) =>
                  updateRow(row.id, { credits: toNumberInputValue(value) })
                }
              />
              <Button
                mt={{ base: 0, md: 24 }}
                variant="light"
                color="red"
                leftSection={<IconTrash size={16} />}
                disabled={rows.length === 1}
                onClick={() =>
                  setRows((currentRows) =>
                    currentRows.filter((candidate) => candidate.id !== row.id),
                  )
                }
              >
                Remove {index + 1}
              </Button>
            </SimpleGrid>
          </Paper>
        ))}
      </Stack>
      <Group gap="sm">
        <Button
          variant="light"
          leftSection={<IconPlus size={18} />}
          onClick={() =>
            setRows((currentRows) => [
              ...currentRows,
              { id: createId("semester"), name: "New semester", gpa: "", credits: "" },
            ])
          }
        >
          Add Semester
        </Button>
        <Button
          variant="outline"
          color="gray"
          leftSection={<IconRefresh size={18} />}
          onClick={() => {
            setRows(defaultGpaRows);
            setTouched(false);
          }}
        >
          Reset
        </Button>
        <Button
          leftSection={<IconCalculator size={18} />}
          onClick={() => {
            setTouched(true);
            if (!result.errors.length) {
              trackCalculation(tool, "cumulative_gpa");
            }
          }}
        >
          Calculate GPA
        </Button>
      </Group>
      {touched ? <ValidationAlerts errors={result.errors} /> : null}
      {!result.errors.length ? (
        <ResultSummary
          stats={[
            ["Cumulative GPA", formatNumber(result.cumulativeGpa, 2)],
            ["Total credits", formatNumber(result.totalCredits, 1)],
            ["Total grade points", formatNumber(result.totalGradePoints, 1)],
          ]}
          note="Credits affect GPA because semesters with more credits carry more weight in the cumulative calculation."
        />
      ) : null}
    </Stack>
  );
}

function AiEssayReviewerTool({ tool }: { tool: ToolCatalogItem }) {
  const [essay, setEssay] = useState("");
  const [essayType, setEssayType] = useState("Common App essay");
  const [reviewed, setReviewed] = useState(false);
  const analysis = useMemo(() => analyzeEssayText(essay), [essay]);
  const errors = essay.trim().length < 50 ? ["Paste at least 50 characters for useful feedback."] : [];

  // TODO: Replace heuristic scoring with a real AI review backend when available.
  const feedback = useMemo(() => createEssayFeedback(analysis, essayType), [analysis, essayType]);

  return (
    <Stack gap="lg">
      <ToolInputsTitle
        title="Get Instant Essay Feedback"
        description="Paste your essay and choose the essay type. This reviewer gives feedback for improvement, not a final essay for submission."
      />
      <Select
        label="Essay type"
        value={essayType}
        data={[
          "Common App essay",
          "Personal statement",
          "Scholarship essay",
          "College application essay",
          "General academic essay",
        ]}
        onChange={(value) => setEssayType(value || "General academic essay")}
      />
      <Textarea
        label="Essay text"
        minRows={8}
        value={essay}
        onChange={(event) => setEssay(event.currentTarget.value)}
        placeholder="Paste your essay draft here..."
      />
      <CalculateControls
        calculateLabel="Review Essay"
        icon={<IconSparkles size={18} />}
        onCalculate={() => {
          setReviewed(true);
          if (!errors.length) {
            trackCalculation(tool, "essay_review");
          }
        }}
        onReset={() => {
          setEssay("");
          setReviewed(false);
        }}
      />
      {reviewed ? <ValidationAlerts errors={errors} /> : null}
      {reviewed && !errors.length ? (
        <EssayFeedbackPanel analysis={analysis} feedback={feedback} />
      ) : null}
    </Stack>
  );
}

function CollegeEssayEditorTool({ tool }: { tool: ToolCatalogItem }) {
  const [essay, setEssay] = useState("");
  const [focus, setFocus] = useState("Clarity");
  const [reviewed, setReviewed] = useState(false);
  const analysis = useMemo(() => analyzeEssayText(essay), [essay]);
  const errors = essay.trim().length < 50 ? ["Paste at least 50 characters for useful editing metrics."] : [];
  const editingChecklist = createEditingChecklist(analysis, focus);

  return (
    <Stack gap="lg">
      <ToolInputsTitle
        title="Check and Improve Your Essay"
        description="Paste your essay to review word count, readability, structure, and editing priorities."
      />
      <Select
        label="Editing focus"
        value={focus}
        data={[
          "Grammar",
          "Structure",
          "Clarity",
          "Tone",
          "Word count reduction",
          "Personal statement polish",
        ]}
        onChange={(value) => setFocus(value || "Clarity")}
      />
      <Textarea
        label="Essay text"
        minRows={8}
        value={essay}
        onChange={(event) => setEssay(event.currentTarget.value)}
        placeholder="Paste your essay draft here..."
      />
      <CalculateControls
        calculateLabel="Check Essay"
        icon={<IconSparkles size={18} />}
        onCalculate={() => {
          setReviewed(true);
          if (!errors.length) {
            trackCalculation(tool, "essay_editing_metrics");
          }
        }}
        onReset={() => {
          setEssay("");
          setReviewed(false);
        }}
      />
      {reviewed ? <ValidationAlerts errors={errors} /> : null}
      {reviewed && !errors.length ? (
        <Paper withBorder radius="md" p="lg" className="utility-result">
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <ResultStat label="Word count" value={analysis.wordCount} />
              <ResultStat label="Sentence count" value={analysis.sentenceCount} />
              <ResultStat label="Readability estimate" value={analysis.readability} />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <ResultStat
                label="Paragraph count"
                value={analysis.paragraphCount}
              />
              <ResultStat
                label="Average sentence length"
                value={`${formatNumber(analysis.averageSentenceLength)} words`}
              />
            </SimpleGrid>
            <Checklist title="Editing checklist" items={editingChecklist.checklist} />
            <Checklist title="Suggested improvement areas" items={editingChecklist.suggestions} />
            <Alert color="blue" radius="md" variant="light">
              This tool gives editing guidance only. Keep ownership of your
              work and check your course or application rules before submission.
            </Alert>
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}

function SpeechWordCounterTool({ tool }: { tool: ToolCatalogItem }) {
  const [speech, setSpeech] = useState("");
  const [tracked, setTracked] = useState(false);
  const words = countWords(speech);
  const characters = speech.length;
  const slowTime = words / speechSpeeds.slow;
  const averageTime = words / speechSpeeds.average;
  const fastTime = words / speechSpeeds.fast;

  return (
    <Stack gap="lg">
      <ToolInputsTitle
        title="Count Speech Words and Speaking Time"
        description="Paste your speech to calculate word count, character count, and estimated speaking time."
      />
      <Textarea
        label="Speech text"
        minRows={8}
        value={speech}
        onChange={(event) => setSpeech(event.currentTarget.value)}
        placeholder="Paste your speech or presentation script here..."
      />
      <CalculateControls
        calculateLabel="Calculate Speech Time"
        onCalculate={() => {
          setTracked(true);
          trackCalculation(tool, "speech_word_count");
        }}
        onReset={() => {
          setSpeech("");
          setTracked(false);
        }}
      />
      <Paper withBorder radius="md" p="lg" className="utility-result">
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <ResultStat label="Total words" value={words} />
            <ResultStat label="Character count" value={characters} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <ResultStat label="Slow pace" value={formatMinutes(slowTime)} />
            <ResultStat label="Average pace" value={formatMinutes(averageTime)} />
            <ResultStat label="Fast pace" value={formatMinutes(fastTime)} />
          </SimpleGrid>
          <Text c="dimmed">
            {tracked
              ? "Use these estimates to rehearse out loud and trim sections that run long."
              : "Results update as you type. Press calculate when you want to record a completed check."}
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
}

function SpeechTimeCalculatorTool({ tool }: { tool: ToolCatalogItem }) {
  const [mode, setMode] = useState<"duration" | "words">("duration");
  const [duration, setDuration] = useState<NumberValue>(5);
  const [wordCount, setWordCount] = useState<NumberValue>(650);
  const [speed, setSpeed] = useState<"slow" | "average" | "fast">("average");
  const [touched, setTouched] = useState(false);
  const wpm = speechSpeeds[speed];
  const targetWords = numberValue(duration) * wpm;
  const estimatedDuration = numberValue(wordCount) / wpm;
  const errors =
    mode === "duration"
      ? numberValue(duration) <= 0
        ? ["Speech duration must be greater than 0 minutes."]
        : []
      : numberValue(wordCount) <= 0
        ? ["Word count must be greater than 0."]
        : [];

  return (
    <Stack gap="lg">
      <ToolInputsTitle
        title="Estimate Speech Length and Word Count"
        description="Choose whether you know the speech duration or the word count, then select your speaking speed."
      />
      <SegmentedControl
        value={mode}
        onChange={(value) => setMode(value as "duration" | "words")}
        data={[
          { value: "duration", label: "Minutes to words" },
          { value: "words", label: "Words to minutes" },
        ]}
      />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {mode === "duration" ? (
          <NumberInput
            label="Speech duration in minutes"
            value={duration}
            min={0}
            onChange={(value) => setDuration(toNumberInputValue(value))}
          />
        ) : (
          <NumberInput
            label="Word count"
            value={wordCount}
            min={0}
            onChange={(value) => setWordCount(toNumberInputValue(value))}
          />
        )}
        <Select
          label="Speaking speed"
          value={speed}
          data={[
            { value: "slow", label: "Slow - 100 WPM" },
            { value: "average", label: "Average - 130 WPM" },
            { value: "fast", label: "Fast - 160 WPM" },
          ]}
          onChange={(value) => setSpeed((value || "average") as "slow" | "average" | "fast")}
        />
      </SimpleGrid>
      <CalculateControls
        calculateLabel="Calculate Speech Time"
        onCalculate={() => {
          setTouched(true);
          if (!errors.length) {
            trackCalculation(tool, mode === "duration" ? "speech_word_target" : "speech_duration");
          }
        }}
        onReset={() => {
          setMode("duration");
          setDuration(5);
          setWordCount(650);
          setSpeed("average");
          setTouched(false);
        }}
      />
      {touched ? <ValidationAlerts errors={errors} /> : null}
      {!errors.length ? (
        <Paper withBorder radius="md" p="lg" className="utility-result">
          <Stack gap="md">
            {mode === "duration" ? (
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <ResultStat label="Recommended word count" value={Math.round(targetWords)} />
                <ResultStat label="Lower range" value={Math.round(targetWords * 0.9)} />
                <ResultStat label="Upper range" value={Math.round(targetWords * 1.1)} />
              </SimpleGrid>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <ResultStat label="Estimated duration" value={formatMinutes(estimatedDuration)} />
                <ResultStat label="Speaking speed" value={`${wpm} WPM`} />
              </SimpleGrid>
            )}
            <Alert color="blue" radius="md" variant="light">
              For a 5-minute speech, aim for around 650 words at an average
              speaking speed of 130 words per minute. If you speak slowly, use
              around 500 words. If you speak quickly, you may need around 800
              words.
            </Alert>
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}

function PercentNumberInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: NumberValue;
  onChange: (value: NumberValue) => void;
}) {
  return (
    <NumberInput
      label={label}
      value={value}
      min={0}
      max={100}
      rightSection={<Text size="xs">%</Text>}
      onChange={(nextValue) => onChange(toNumberInputValue(nextValue))}
    />
  );
}

function ToolInputsTitle({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Stack gap={4}>
      <Title order={2}>{title}</Title>
      <Text c="dimmed">{description}</Text>
    </Stack>
  );
}

function CalculateControls({
  calculateLabel,
  onCalculate,
  onReset,
  icon,
}: {
  calculateLabel: string;
  onCalculate: () => void;
  onReset: () => void;
  icon?: ReactNode;
}) {
  return (
    <Group gap="sm">
      <Button leftSection={icon || <IconCalculator size={18} />} onClick={onCalculate}>
        {calculateLabel}
      </Button>
      <Button
        variant="outline"
        color="gray"
        leftSection={<IconRefresh size={18} />}
        onClick={onReset}
      >
        Reset
      </Button>
    </Group>
  );
}

function ResultSummary({
  stats,
  note,
}: {
  stats: Array<[string, ReactNode]>;
  note: ReactNode;
}) {
  return (
    <Paper withBorder radius="md" p="lg" className="utility-result">
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: stats.length >= 3 ? 3 : 2 }} spacing="md">
          {stats.map(([label, value]) => (
            <ResultStat key={label} label={label} value={value} />
          ))}
        </SimpleGrid>
        <Text c="dimmed">{note}</Text>
      </Stack>
    </Paper>
  );
}

function countWords(text: string) {
  return (text.trim().match(/\b[\w'-]+\b/g) || []).length;
}

function countSentences(text: string) {
  const matches = text.trim().match(/[^.!?]+[.!?]+/g);
  return matches?.length || (text.trim() ? 1 : 0);
}

function analyzeEssayText(text: string) {
  const trimmed = text.trim();
  const wordCount = countWords(trimmed);
  const sentenceCount = countSentences(trimmed);
  const paragraphs = trimmed
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  const paragraphCount = paragraphs.length || (trimmed ? 1 : 0);
  const averageSentenceLength = sentenceCount ? wordCount / sentenceCount : 0;
  const firstPersonCount = (trimmed.match(/\b(I|me|my|mine|we|our)\b/gi) || [])
    .length;
  const readability =
    averageSentenceLength <= 18
      ? "Clear"
      : averageSentenceLength <= 25
        ? "Moderate"
        : "Dense";
  const words = (trimmed.toLowerCase().match(/\b[a-z]{4,}\b/g) || []).filter(
    (word) =>
      ![
        "that",
        "this",
        "with",
        "from",
        "have",
        "were",
        "will",
        "your",
      ].includes(word),
  );
  const wordFrequency = words.reduce<Record<string, number>>((counts, word) => {
    counts[word] = (counts[word] || 0) + 1;
    return counts;
  }, {});
  const repeatedWords = Object.entries(wordFrequency)
    .filter(([, count]) => count >= 4)
    .map(([word]) => word)
    .slice(0, 5);

  return {
    wordCount,
    sentenceCount,
    paragraphCount,
    averageSentenceLength,
    firstPersonCount,
    readability,
    repeatedWords,
  };
}

function createEssayFeedback(
  analysis: ReturnType<typeof analyzeEssayText>,
  essayType: string,
) {
  const clarityScore = clampScore(
    88 - Math.max(0, analysis.averageSentenceLength - 18) * 2,
  );
  const structureScore = clampScore(60 + Math.min(analysis.paragraphCount, 6) * 6);
  const grammarScore = clampScore(
    analysis.readability === "Clear" ? 84 : analysis.readability === "Moderate" ? 74 : 64,
  );
  const personalVoiceScore = clampScore(
    essayType.includes("academic")
      ? 70 + Math.min(analysis.firstPersonCount, 5) * 2
      : 58 + Math.min(analysis.firstPersonCount, 12) * 3,
  );
  const strengths = [
    analysis.paragraphCount >= 3
      ? "The draft has visible paragraph structure."
      : "The draft has a clear starting point for revision.",
    analysis.firstPersonCount > 0
      ? "There is some personal voice for reviewers to evaluate."
      : "The essay avoids overusing first-person language.",
    analysis.wordCount >= 250
      ? "There is enough text to identify patterns in clarity and structure."
      : "The draft is short enough to revise quickly.",
  ];
  const suggestions = [
    analysis.averageSentenceLength > 25
      ? "Shorten long sentences so each idea is easier to follow."
      : "Keep sentence lengths varied so the essay feels natural.",
    analysis.paragraphCount < 4
      ? "Break the essay into clearer paragraphs with one main point each."
      : "Check that each paragraph advances the same central story.",
    analysis.repeatedWords.length
      ? `Review repeated words such as ${analysis.repeatedWords.join(", ")}.`
      : "Look for repeated ideas, not just repeated words.",
  ];
  const checklist = [
    "The essay answers the prompt directly.",
    "The opening paragraph creates a clear reason to keep reading.",
    "Specific examples support the main point.",
    "The conclusion reflects growth without exaggerating.",
  ];

  return {
    clarityScore,
    structureScore,
    grammarScore,
    personalVoiceScore,
    strengths,
    suggestions,
    checklist,
  };
}

function createEditingChecklist(
  analysis: ReturnType<typeof analyzeEssayText>,
  focus: string,
) {
  const checklist = [
    `Primary editing focus: ${focus}.`,
    "Check that each paragraph has one clear job.",
    "Remove repeated phrases and vague claims.",
    "Read the draft aloud to catch awkward transitions.",
  ];
  const suggestions = [
    analysis.averageSentenceLength > 22
      ? "Reduce long sentences before polishing grammar."
      : "Sentence length looks manageable; focus on transitions and evidence.",
    analysis.paragraphCount < 3
      ? "Add paragraph breaks where the argument shifts."
      : "Review paragraph order for the strongest flow.",
    analysis.wordCount > 700
      ? "Mark any sentence that repeats information already stated."
      : "If the essay feels thin, add one specific example instead of general claims.",
  ];

  return { checklist, suggestions };
}

function EssayFeedbackPanel({
  analysis,
  feedback,
}: {
  analysis: ReturnType<typeof analyzeEssayText>;
  feedback: ReturnType<typeof createEssayFeedback>;
}) {
  return (
    <Paper withBorder radius="md" p="lg" className="utility-result">
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <ResultStat label="Word count" value={analysis.wordCount} />
          <ResultStat label="Paragraph count" value={analysis.paragraphCount} />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="md">
          <ResultStat label="Clarity score" value={`${feedback.clarityScore}/100`} />
          <ResultStat label="Structure score" value={`${feedback.structureScore}/100`} />
          <ResultStat label="Grammar/style score" value={`${feedback.grammarScore}/100`} />
          <ResultStat label="Personal voice score" value={`${feedback.personalVoiceScore}/100`} />
        </SimpleGrid>
        <Checklist title="Strengths" items={feedback.strengths} />
        <Checklist title="Improvement suggestions" items={feedback.suggestions} />
        <Checklist title="Submission checklist" items={feedback.checklist} />
        <Alert color="blue" radius="md" variant="light">
          This tool is for feedback and improvement support only. It does not
          guarantee admission and does not write a final essay for submission.
        </Alert>
      </Stack>
    </Paper>
  );
}

function Checklist({ title, items }: { title: string; items: string[] }) {
  return (
    <Paper withBorder radius="md" p="md" className="utility-checklist">
      <Stack gap="xs">
        <Text fw={800}>{title}</Text>
        <ul className="utility-alert-list">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </Stack>
    </Paper>
  );
}

function clampScore(score: number) {
  return Math.max(35, Math.min(98, Math.round(score)));
}

function formatMinutes(minutes: number) {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return "0 min";
  }

  const totalSeconds = Math.round(minutes * 60);
  const wholeMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (!wholeMinutes) {
    return `${seconds} sec`;
  }

  return seconds ? `${wholeMinutes} min ${seconds} sec` : `${wholeMinutes} min`;
}

const toolPageContent: Record<ToolSlug, ToolContent> = {
  "semester-grade-calculator": {
    description:
      "Use this semester grade calculator to calculate your final semester grade from quarter grades, final exam scores, assignments, projects, and weighted grade components. You can choose the calculate semester grade mode when you know your first quarter grade, second quarter grade, final exam grade, and each weight. You can also choose the required final exam grade mode when you want to answer what grade do I need on my final to reach a target semester grade. The calculator checks that grades and weights stay between 0 and 100, warns when total weights do not equal 100%, and shows an estimated percentage and letter grade.",
    howToUseSteps: [
      "Choose Calculate semester grade or What grade do I need on my final.",
      "Enter the first quarter grade, first quarter weight, second quarter grade, and second quarter weight.",
      "Enter either the final exam grade or the target semester grade, depending on the mode.",
      "Enter the final exam weight, confirm that all weights equal 100%, then review the percentage and estimated letter grade.",
    ],
    formulaSection: {
      title: "Semester Grade Calculator Formula",
      body: (
        <>
          <p>
            To calculate semester grade, use: <strong>Semester Grade = (First
            Quarter Grade x First Quarter Weight / 100) + (Second Quarter Grade
            x Second Quarter Weight / 100) + (Final Exam Grade x Final Exam
            Weight / 100)</strong>.
          </p>
          <p>
            To answer what grade do I need on my final, use:{" "}
            <strong>Required Final Exam Grade = (Target Semester Grade - Current
            Weighted Grade) / (Final Exam Weight / 100)</strong>. Current
            weighted grade is the first quarter contribution plus the second
            quarter contribution.
          </p>
        </>
      ),
    },
    exampleContent: {
      title: "Semester Grade Calculation Example",
      body: (
        <>
          <p>
            Suppose your first quarter grade is 84% with a 40% weight, your
            second quarter grade is 88% with a 40% weight, and your final exam
            grade is 90% with a 20% weight. The semester grade is 33.6 + 35.2 +
            18.0, which equals 86.8%.
          </p>
          <p>
            If your target semester grade is 90% instead, the required final
            exam mode subtracts your current weighted grade from the target and
            divides by the final exam weight as a decimal. The result shows the
            final exam score needed, but every school can define grade bands and
            rounding rules differently.
          </p>
        </>
      ),
    },
    whySection: {
      title: "Why Students Use a Semester Grade Calculator",
      body: (
        <>
          <p>
            Students use a semester grade calculator online when they need a
            clear view of how quarter grades, assignments, projects, and final
            exam scores combine. It is especially helpful before finals because
            you can see whether a final exam has enough weight to change your
            final semester grade or whether coursework is the better place to
            improve.
          </p>
          <p>
            This tool is not an official transcript calculator. It gives a
            planning estimate from the numbers you enter, so always compare the
            output with your course policy.
          </p>
        </>
      ),
    },
    commonMistakes: [
      "Using quarter or final exam weights from memory instead of checking the syllabus.",
      "Entering a points score as a percentage without converting it first.",
      "Trying to calculate a result when the first quarter, second quarter, and final exam weights do not total 100%.",
      "Treating the estimated letter grade as official when your institution uses a different scale.",
    ],
    ctaTitle: "Need help improving your semester grade?",
    ctaDescription:
      "If your assignments are affecting your final grade, SubjectBuddy can connect you with academic experts for assignment, essay, and coursework support.",
    ctaButtonText: "Get Assignment Help",
    ctaButtonHref: "/assignment-help",
    faqItems: [
      {
        question: "What is a semester grade calculator?",
        answer:
          "A semester grade calculator estimates your final semester grade by combining first quarter grade, second quarter grade, final exam grade, and the weight assigned to each component.",
      },
      {
        question: "How do I calculate my semester grade?",
        answer:
          "Multiply each grade percentage by its weight divided by 100, then add the weighted contributions together. If the weights add up to 100%, the total is your estimated semester grade.",
      },
      {
        question: "What grade do I need on my final?",
        answer:
          "Use the required final exam mode. Enter both quarter grades, their weights, the final exam weight, and your target semester grade. The calculator applies the required final exam grade formula and shows the score you need.",
      },
      {
        question: "What if my total weight is less than 100%?",
        answer:
          "A total weight below 100% usually means a grading component is missing or not yet included. Check your syllabus and add the remaining assessments before relying on the result.",
      },
      {
        question: "Can this calculator predict my final course grade?",
        answer:
          "It can estimate your final course grade from the values you enter, but it cannot replace your teacher, professor, learning platform, or official grading policy.",
      },
      {
        question: "Are grading scales the same in every university?",
        answer:
          "No. Letter-grade and pass/fail scales vary by school, college, university, and country, so use the percentage result with your institution's grading policy.",
      },
    ],
  },
  "grade-calculator": {
    description:
      "Use this grade calculator to estimate assignment, quiz, exam, project, or class grades from scores and weights. You can enter score received, total score, and weight percentage, or switch to percentage-only mode when your grades are already reported as percentages. Students sometimes search for grade caculator or grade caluclator, but this tool works as a complete grade calculator for assignments, quizzes, tests, projects, and exams. It can also help when people type gpa calculato and actually need a quick class-grade estimate before checking GPA impact.",
    howToUseSteps: [
      "Choose score mode or percentage-only mode.",
      "Add each assignment, quiz, exam, project, or test row.",
      "Enter the score received and total score, or enter the percentage directly.",
      "Add the weight for each row and review the weighted class grade.",
    ],
    formulaSection: {
      title: "Grade Calculator Formula",
      body: (
        <>
          <p>
            In score mode, this grade percentage calculator first converts each
            row into a percentage: <strong>score received / total score x
            100</strong>. It then multiplies that percentage by the assessment
            weight.
          </p>
          <p>
            In percentage-only mode, the calculation is simply{" "}
            <strong>grade percentage x weight</strong> for each row, with all
            weighted values added together.
          </p>
        </>
      ),
    },
    exampleContent: {
      title: "Assignment Grade Calculator Example",
      body: (
        <>
          <p>
            If you score 42 out of 50 on an assignment worth 30%, the assignment
            percentage is 84%. Its weighted contribution is 84 x 0.30 = 25.2
            points toward the final class grade.
          </p>
          <p>
            Add the weighted contribution from every row to estimate your class
            grade calculator result.
          </p>
        </>
      ),
    },
    whySection: {
      title: "Why a Grade Calculator Helps",
      body: (
        <>
          <p>
            A grade calculator helps students stop guessing how one assignment
            or test affects the whole class. It is useful when some work is
            graded out of points, some work is reported as percentages, and each
            category has a different weight.
          </p>
          <p>
            The result can guide your study plan by showing whether the highest
            impact work is an exam, a project, or a set of assignments still
            left to submit.
          </p>
        </>
      ),
    },
    commonMistakes: [
      "Mixing point scores and percentage scores in the same row.",
      "Leaving a total score as zero.",
      "Using assignment category weights that do not match the syllabus.",
      "Forgetting that extra credit rules vary by instructor.",
    ],
    ctaTitle: "Want to improve your next assignment grade?",
    ctaDescription:
      "SubjectBuddy helps students get expert guidance for assignments, essays, reports, and coursework.",
    ctaButtonText: "Improve My Grade",
    ctaButtonHref: "/assignment-help",
    faqItems: [
      {
        question: "How does this grade calculator work?",
        answer:
          "It converts each score into a percentage, applies the weight for that assessment, and adds the weighted values to estimate your total grade.",
      },
      {
        question: "Can I calculate weighted grades?",
        answer:
          "Yes. Enter the weight percentage for each assignment, quiz, exam, or project, and the calculator estimates your weighted grade.",
      },
      {
        question: "What does grade weight mean?",
        answer:
          "Grade weight is how much an assessment counts toward the final grade. A final exam worth 40% affects your grade more than a quiz worth 5%.",
      },
      {
        question: "Why is my total weight not 100%?",
        answer:
          "Your total weight may be missing a category, or you may be calculating only part of the course. Check your syllabus before using the result as final.",
      },
      {
        question: "Can this calculate assignment grades?",
        answer:
          "Yes. You can use it as an assignment grade calculator by entering the score received, total possible score, and assignment weight.",
      },
    ],
  },
  "midterm-grade-calculator": {
    description:
      "Use this midterm grade calculator to see how a midterm exam score affects your updated course grade. Enter your current grade before the midterm, how much completed coursework counts, the midterm score, and the midterm weight. The calculator shows the updated weighted grade, midterm impact, and a letter grade estimate. It is designed for students who want to calculate midterm grade outcomes, understand whether a low midterm can be recovered, and plan how assignments or finals can improve the rest of the semester.",
    howToUseSteps: [
      "Enter your current grade before the midterm.",
      "Enter the completed coursework weight from your grading policy.",
      "Add your midterm score and the midterm weight.",
      "Review the updated course grade, midterm impact, and letter estimate.",
    ],
    formulaSection: {
      title: "Midterm Grade Calculator Formula",
      body: (
        <p>
          Updated grade = current grade contribution + midterm score
          contribution. The current grade contribution is current grade x
          completed coursework weight. The midterm contribution is midterm score
          x midterm weight.
        </p>
      ),
    },
    exampleContent: {
      title: "Midterm Grade Example",
      body: (
        <p>
          If your current grade is 82%, completed coursework is worth 70%, and
          the midterm score is 76% worth 30%, the updated grade is 82 x 0.70 +
          76 x 0.30 = 80.2%.
        </p>
      ),
    },
    whySection: {
      title: "Why Midterm Impact Matters",
      body: (
        <p>
          A midterm calculator helps you understand whether the exam changed
          your standing slightly or significantly. If the midterm has a high
          weight, even a small score difference can move your grade. If it has a
          low weight, assignments, labs, projects, and finals may still carry
          enough value to recover.
        </p>
      ),
    },
    commonMistakes: [
      "Assuming every midterm has the same weight.",
      "Using the current class average without checking whether the midterm is already included.",
      "Ignoring remaining assignment categories that can still improve the grade.",
    ],
    ctaTitle: "Did your midterm lower your grade?",
    ctaDescription:
      "Recover before finals with expert assignment and coursework support from SubjectBuddy.",
    ctaButtonText: "Get Academic Support",
    ctaButtonHref: "/assignment-help",
    faqItems: [
      {
        question: "How do I calculate my grade after a midterm?",
        answer:
          "Multiply your current grade by the completed coursework weight, multiply your midterm score by the midterm weight, and add the two contributions.",
      },
      {
        question: "What if my midterm has a high weight?",
        answer:
          "A high-weight midterm can strongly affect your course grade, so compare the result with remaining assignments and finals to plan recovery.",
      },
      {
        question: "Can assignments improve my grade after a low midterm?",
        answer:
          "Yes, if assignments or projects still have meaningful weight. Focus first on the remaining work that counts most.",
      },
      {
        question: "Is this calculator accurate for all universities?",
        answer:
          "It is an estimate based on the values you enter. University grading systems and course policies can vary.",
      },
    ],
  },
  "final-grade-calculator": {
    description:
      "Use this final grade calculator to find what score you need on your final exam to reach a target course grade. Enter your current grade, desired final grade, and final exam weight. The calculator shows the required final exam score, a message when the target may be impossible through the final alone, and a target letter grade estimate. It is useful for students asking what do I need on my final, calculating final grade goals, or checking whether assignments should be improved before finals arrive.",
    howToUseSteps: [
      "Enter your current course grade before the final exam.",
      "Enter the desired final grade you want to finish with.",
      "Enter the final exam weight as a percentage.",
      "Review the required final exam score and target letter grade.",
    ],
    formulaSection: {
      title: "Final Grade Calculator Formula",
      body: (
        <p>
          Required final score = (desired grade - current grade x (1 - final
          weight)) / final weight. Convert final exam weight to a decimal before
          calculating.
        </p>
      ),
    },
    exampleContent: {
      title: "Final Exam Grade Calculator Example",
      body: (
        <p>
          If your current grade is 84%, your desired final grade is 90%, and
          the final exam is worth 25%, the required score is (90 - 84 x 0.75) /
          0.25 = 108%. That target may not be possible through the final exam
          alone.
        </p>
      ),
    },
    whySection: {
      title: "Why Students Calculate Final Grade Goals",
      body: (
        <p>
          A final semester grade calculator helps students make realistic plans
          before the final exam. If the required score is possible, you can plan
          study time around that target. If it is above 100%, you may need to
          improve remaining coursework, ask about feedback, or adjust the target
          to match the grading policy.
        </p>
      ),
    },
    commonMistakes: [
      "Entering the final exam weight as 0.25 instead of 25.",
      "Using a current grade that already includes the final exam.",
      "Forgetting remaining coursework that can change the current grade.",
    ],
    ctaTitle: "Need to improve your coursework before finals?",
    ctaDescription:
      "SubjectBuddy can help you strengthen assignments, reports, essays, and coursework before final grading.",
    ctaButtonText: "Get Help Before Finals",
    ctaButtonHref: "/assignment-help",
    faqItems: [
      {
        question: "How do I calculate what I need on my final?",
        answer:
          "Use your current grade, target grade, and final exam weight. The calculator applies the standard required-final-score formula.",
      },
      {
        question: "What final exam score do I need for an A?",
        answer:
          "Enter 90 as the desired final grade for a common US A threshold, then enter your current grade and final exam weight.",
      },
      {
        question: "What if I need more than 100%?",
        answer:
          "If the required score is above 100%, the target may not be possible through the final exam alone.",
      },
      {
        question: "Can assignments still improve my final grade?",
        answer:
          "Yes, if assignments or projects are still ungraded or open for improvement, they may help your final grade before the exam.",
      },
    ],
  },
  "letter-grade-calculator": {
    description:
      "Use this letter grade calculator to convert a percentage score into an estimated letter grade, GPA equivalent, and short grade meaning. Enter any percentage, such as 75%, 74%, 84%, or 90%, and the calculator maps it to a common US-style grading scale. This is useful when you want to know what grade is 75 percent, what is a 74 in letter grade, whether an 84 letter grade is strong, or whether a 90 percent grade is usually considered an A.",
    howToUseSteps: [
      "Enter your percentage score.",
      "Review the estimated letter grade.",
      "Check the GPA equivalent estimate.",
      "Compare the result with your institution's grading policy.",
    ],
    formulaSection: {
      title: "Letter Grade Scale Used",
      body: (
        <p>
          This calculator uses a simple default US scale: 90-100 = A, 80-89 =
          B, 70-79 = C, 60-69 = D, and below 60 = F. Grade scales can vary by
          school, college, university, and country.
        </p>
      ),
    },
    exampleContent: {
      title: "Common Letter Grade Answers",
      body: (
        <>
          <p>
            <strong>What letter grade is a 75?</strong> A 75 is usually a C on
            a common US scale because it falls between 70 and 79. It may be a
            pass, but your course policy decides the official meaning.
          </p>
          <p>
            <strong>What is a 74 in letter grade?</strong> A 74 is usually a C,
            close to the middle of the 70-79 range. Some schools use plus/minus
            grades, so always check the course scale.
          </p>
          <p>
            <strong>Is 84 a good grade?</strong> An 84 letter grade is commonly
            a B. It usually shows solid performance, though improvement may be
            possible if you are aiming for an A range.
          </p>
          <p>
            <strong>What is a 90 percent grade?</strong> A 90 percent grade is
            usually an A on the default scale. Some classes may require a higher
            cutoff for an A, especially with plus/minus grading.
          </p>
          <p>
            <strong>What grade is 75 percent?</strong> A 75 percent score is
            commonly a C, but the official result depends on your institution's
            grading bands.
          </p>
        </>
      ),
    },
    whySection: {
      title: "Why Letter Grades Vary",
      body: (
        <p>
          Letter grades are shorthand for performance, but they are not
          universal. A 75 may be treated differently in the US, UK, Canada, or
          Australia, and universities may also use pass, merit, distinction, or
          GPA bands. Use this result as a quick estimate, then confirm the
          official meaning with your syllabus.
        </p>
      ),
    },
    commonMistakes: [
      "Assuming all schools use the same letter-grade bands.",
      "Forgetting plus/minus grading.",
      "Treating a GPA estimate as official.",
    ],
    ctaTitle: "Want to move your next grade higher?",
    ctaDescription:
      "SubjectBuddy helps students improve assignment clarity, structure, research, and academic writing.",
    ctaButtonText: "Improve My Assignment",
    ctaButtonHref: "/assignment-help",
    faqItems: [
      {
        question: "What letter grade is a 75?",
        answer:
          "A 75 is usually a C on a common US-style scale, though official grade bands vary by institution.",
      },
      {
        question: "Is 74 a passing grade?",
        answer:
          "A 74 is often a passing C, but the passing threshold depends on your school, program, and course policy.",
      },
      {
        question: "Is 84 a B grade?",
        answer:
          "Yes, 84 is commonly a B grade on the default scale used by this calculator.",
      },
      {
        question: "Is 90 an A grade?",
        answer:
          "A 90 is usually an A on a common US scale, but some schools use different cutoffs.",
      },
      {
        question: "Are letter grades the same in the US and UK?",
        answer:
          "No. The US and UK use different grading traditions, so compare any letter result with your local grading policy.",
      },
    ],
  },
  "cumulative-gpa-calculator": {
    description:
      "Use this cumulative GPA calculator to calculate GPA across multiple semesters on a 4.0 scale. Enter each semester name, semester GPA, and credits or hours. The calculator multiplies each semester GPA by its credits, adds total grade points, and divides by total credits. It helps students understand how to calculate cumulative GPA for all semesters, compare semester GPA vs cumulative GPA, and see why higher-credit semesters affect the final GPA more than lower-credit semesters.",
    howToUseSteps: [
      "Enter each semester name.",
      "Add the semester GPA on a 4.0 scale.",
      "Enter the credits or hours for that semester.",
      "Review cumulative GPA, total credits, and total grade points.",
    ],
    formulaSection: {
      title: "Cumulative GPA Calculator Formula",
      body: (
        <p>
          Cumulative GPA = total grade points / total credits. Grade points =
          semester GPA x semester credits. This makes the calculator a weighted
          GPA calculator college students can use when credit loads are uneven.
        </p>
      ),
    },
    exampleContent: {
      title: "Example GPA Calculation",
      body: (
        <p>
          If one semester has a 3.4 GPA over 15 credits and another has a 3.6
          GPA over 15 credits, total grade points are 51 + 54 = 105. Divide by
          30 credits and the cumulative GPA is 3.50.
        </p>
      ),
    },
    whySection: {
      title: "Semester GPA vs Cumulative GPA",
      body: (
        <p>
          Semester GPA measures one term. Cumulative GPA combines all terms and
          weights them by credits. This matters because one strong semester may
          not move the cumulative GPA much if you already have many completed
          credits, while an early semester can have a larger effect.
        </p>
      ),
    },
    commonMistakes: [
      "Averaging semester GPAs without considering credits.",
      "Entering credits as percentages.",
      "Mixing 4.0-scale GPA with percentage grades.",
    ],
    ctaTitle: "Trying to protect or improve your GPA?",
    ctaDescription:
      "SubjectBuddy connects students with academic experts for assignments, essays, reports, and coursework support.",
    ctaButtonText: "Save My GPA",
    ctaButtonHref: "/services/gpa-improvement-assignment-help",
    faqItems: [
      {
        question: "How do I calculate cumulative GPA?",
        answer:
          "Multiply each semester GPA by its credits, add all grade points, then divide by total credits.",
      },
      {
        question: "What is the difference between semester GPA and cumulative GPA?",
        answer:
          "Semester GPA covers one term. Cumulative GPA combines all completed semesters and weights them by credits.",
      },
      {
        question: "Do credits affect GPA?",
        answer:
          "Yes. A semester with more credits has more influence on cumulative GPA than a semester with fewer credits.",
      },
      {
        question: "Is this calculator for a 4.0 scale?",
        answer:
          "Yes. This calculator uses a 4.0 GPA scale by default.",
      },
      {
        question: "How can I improve my GPA?",
        answer:
          "Focus on high-credit courses, improve assignment quality, seek feedback early, and plan recovery before deadlines.",
      },
    ],
  },
  "ai-college-essay-reviewer": {
    description:
      "Use this AI college essay reviewer to get instant feedback on a draft's clarity, structure, grammar/style, tone, and personal voice. Paste your college application essay, Common App essay, scholarship essay, personal statement, or general academic essay, then review the structured feedback. The tool uses deterministic heuristics in the browser, so it does not guarantee admission, does not write a final essay, and should be used as improvement support rather than a replacement for your own thinking or human editing.",
    howToUseSteps: [
      "Select the essay type that best matches your draft.",
      "Paste your essay text into the reviewer.",
      "Click Review Essay to see scores, strengths, suggestions, and a checklist.",
      "Use the feedback to revise your own work ethically.",
    ],
    formulaSection: {
      title: "How the AI Essay Reviewer Works",
      body: (
        <p>
          This front-end reviewer uses heuristic checks: word count, paragraph
          count, sentence length, repeated words, first-person language, and a
          simple readability estimate. It is designed for feedback and
          improvement, not automated final writing.
        </p>
      ),
    },
    exampleContent: {
      title: "Example College Essay Feedback",
      body: (
        <p>
          A draft with six paragraphs, clear sentence length, and specific
          first-person examples may receive strong clarity and personal voice
          feedback. A draft with one long paragraph and repeated general claims
          will likely receive structure and specificity suggestions.
        </p>
      ),
    },
    whySection: {
      title: "Why Students Use an Essay Reviewer",
      body: (
        <p>
          Students often know a draft needs work but cannot see which part is
          weakest. A college essay reviewer can separate clarity, structure,
          tone, and personal voice so revision feels manageable. For admissions
          writing, the goal is ethical feedback, stronger reflection, and
          clearer storytelling, not guaranteed outcomes.
        </p>
      ),
    },
    commonMistakes: [
      "Using AI feedback as a substitute for personal reflection.",
      "Adding generic claims instead of specific examples.",
      "Ignoring the prompt while polishing grammar.",
    ],
    ctaTitle: "Want expert human feedback on your essay?",
    ctaDescription:
      "Get your college essay reviewed by SubjectBuddy experts for clarity, structure, tone, and impact.",
    ctaButtonText: "Get Essay Review",
    ctaButtonHref: "/services/college-essay-editing",
    faqItems: [
      {
        question: "What does an AI college essay reviewer check?",
        answer:
          "It checks patterns such as word count, paragraph count, sentence length, clarity, structure, grammar/style, and personal voice.",
      },
      {
        question: "Can this tool guarantee admission?",
        answer:
          "No. No essay tool can guarantee admission. This reviewer provides feedback to help you revise more thoughtfully.",
      },
      {
        question: "Should I use AI to write my college essay?",
        answer:
          "You should not use AI to replace your own voice or submit writing that is not yours. Use feedback ethically to improve your draft.",
      },
      {
        question: "How can I improve my personal statement?",
        answer:
          "Clarify the main story, use specific examples, connect reflection to growth, and remove vague claims.",
      },
      {
        question: "Can a human expert review my essay?",
        answer:
          "Yes. SubjectBuddy can connect students with human editing support for clarity, structure, tone, and proofreading.",
      },
    ],
  },
  "college-essay-editor": {
    description:
      "Use this college essay editor to check word count, sentence count, paragraph count, average sentence length, readability, and editing priorities before submission. Choose an editing focus such as grammar, structure, clarity, tone, word count reduction, or personal statement polish. The tool does not generate a final essay and does not claim perfection. It helps students identify revision areas, keep ownership of their work, and decide when expert college essay editing or proofreading would be useful.",
    howToUseSteps: [
      "Choose your editing focus.",
      "Paste your essay draft into the editor.",
      "Click Check Essay to see metrics and editing suggestions.",
      "Revise your essay using the checklist while keeping your own voice.",
    ],
    formulaSection: {
      title: "What the Essay Editor Checks",
      body: (
        <p>
          The editor calculates word count, sentence count, paragraph count,
          average sentence length, and a readability estimate. It then creates
          a checklist based on the selected editing focus.
        </p>
      ),
    },
    exampleContent: {
      title: "Essay Editing Example",
      body: (
        <p>
          If a 700-word essay has only two paragraphs and an average sentence
          length above 25 words, the editor will suggest clearer paragraph
          breaks and shorter sentences before fine grammar polishing.
        </p>
      ),
    },
    whySection: {
      title: "Why College Essay Editing Helps",
      body: (
        <p>
          College essays need more than clean grammar. They need structure,
          specific examples, a consistent voice, and a direct answer to the
          prompt. A college essay editor helps students see the draft like a
          reader and decide what to revise first.
        </p>
      ),
    },
    commonMistakes: [
      "Editing grammar before fixing structure.",
      "Keeping paragraphs that contain too many unrelated ideas.",
      "Removing personality while trying to sound formal.",
    ],
    ctaTitle: "Need a real editor to review your essay?",
    ctaDescription:
      "SubjectBuddy experts can help polish your essay for clarity, structure, grammar, and flow.",
    ctaButtonText: "Get College Essay Editing",
    ctaButtonHref: "/services/college-essay-editing",
    faqItems: [
      {
        question: "What does a college essay editor do?",
        answer:
          "A college essay editor helps improve clarity, structure, grammar, tone, flow, and alignment with the prompt.",
      },
      {
        question: "Can this tool edit my full essay?",
        answer:
          "It provides metrics and suggestions, but it does not rewrite the full essay or claim the output is perfect.",
      },
      {
        question: "Is essay editing allowed?",
        answer:
          "Ethical editing, feedback, proofreading, and structure support are commonly allowed, but always follow your institution or application rules.",
      },
      {
        question: "What should I check before submitting my college essay?",
        answer:
          "Check the prompt, word count, paragraph structure, specificity, grammar, tone, and whether the essay still sounds like you.",
      },
      {
        question: "Can SubjectBuddy experts review my essay?",
        answer:
          "Yes. SubjectBuddy can help with ethical editing, proofreading, clarity, structure, and flow.",
      },
    ],
  },
  "speech-word-counter": {
    description:
      "Use this speech word counter to paste a speech, count total words, count characters, and estimate speaking time at slow, average, or fast pace. It is helpful for class presentations, graduation speech word count planning, debates, oral assignments, and timed speaking tasks. The tool uses common speaking speeds of 100, 130, and 160 words per minute, so you can convert words to minutes speech estimates and adjust your script before practice.",
    howToUseSteps: [
      "Paste your speech or presentation script.",
      "Review total words and character count.",
      "Compare slow, average, and fast speaking time estimates.",
      "Practice out loud and adjust the script until it fits your time limit.",
    ],
    formulaSection: {
      title: "How to Estimate Speech Time",
      body: (
        <p>
          Estimated speech time = word count / words per minute. This tool uses
          100 WPM for slow pace, 130 WPM for average pace, and 160 WPM for fast
          pace.
        </p>
      ),
    },
    exampleContent: {
      title: "Example Speech Word Count",
      body: (
        <p>
          A 650-word speech takes about 5 minutes at 130 words per minute. At a
          slow 100 WPM pace, it takes about 6.5 minutes. At a fast 160 WPM pace,
          it takes just over 4 minutes.
        </p>
      ),
    },
    whySection: {
      title: "Tips for Graduation Speeches and Presentations",
      body: (
        <p>
          A strong speech is usually shorter on paper than students expect.
          Leave time for pauses, audience reaction, and transitions. If your
          speech is too long, remove repeated examples first, then shorten the
          introduction and conclusion without cutting the main message.
        </p>
      ),
    },
    commonMistakes: [
      "Timing a speech silently instead of speaking it out loud.",
      "Ignoring pauses and audience reaction time.",
      "Writing too many examples for a short presentation.",
    ],
    ctaTitle: "Need help writing a strong speech?",
    ctaDescription:
      "SubjectBuddy can help students structure, edit, and improve speeches, presentations, and academic writing.",
    ctaButtonText: "Get Speech Writing Help",
    ctaButtonHref: "/services/speech-writing-help",
    faqItems: [
      {
        question: "How many words are in a 5-minute speech?",
        answer:
          "At an average pace of 130 words per minute, a 5-minute speech is about 650 words.",
      },
      {
        question: "How fast do people speak in a presentation?",
        answer:
          "Many presenters speak around 120 to 150 words per minute, with 130 WPM as a useful average estimate.",
      },
      {
        question: "How do I reduce my speech length?",
        answer:
          "Cut repeated points, shorten examples, remove filler phrases, and keep only the strongest supporting evidence.",
      },
      {
        question: "Can this tool help with graduation speeches?",
        answer:
          "Yes. It can estimate graduation speech word count and timing so you can rehearse within the limit.",
      },
    ],
  },
  "speech-time-calculator": {
    description:
      "Use this speech time calculator to estimate how many words you need for a 1, 3, 5, or 10-minute speech, or convert a word count into estimated speaking duration. Choose slow, average, or fast speaking speed using 100, 130, or 160 words per minute. It is useful for students preparing speeches, presentations, oral assignments, graduation talks, and timed classroom speaking tasks.",
    howToUseSteps: [
      "Choose minutes-to-words or words-to-minutes mode.",
      "Enter speech duration or word count.",
      "Select slow, average, or fast speaking speed.",
      "Use the recommended word count or duration estimate to rehearse.",
    ],
    formulaSection: {
      title: "Speech Duration Calculator Formula",
      body: (
        <p>
          Recommended word count = speech minutes x words per minute. Estimated
          duration = word count / words per minute. The default speeds are slow
          100 WPM, average 130 WPM, and fast 160 WPM.
        </p>
      ),
    },
    exampleContent: {
      title: "How Many Words for a 5 Minute Speech?",
      body: (
        <p>
          For a 5-minute speech, aim for around 650 words at an average speaking
          speed of 130 words per minute. If you speak slowly, use around 500
          words. If you speak quickly, you may need around 800 words.
        </p>
      ),
    },
    whySection: {
      title: "Why Speech Timing Matters",
      body: (
        <p>
          A speech that is too long often feels rushed, while a speech that is
          too short can feel underdeveloped. Estimating speech length before
          practice helps you decide whether to add examples, cut filler, or
          adjust pacing before presenting.
        </p>
      ),
    },
    commonMistakes: [
      "Using a fast speaking speed without rehearsal.",
      "Forgetting pauses between slides or ideas.",
      "Trying to fit too many examples into a short speech.",
    ],
    ctaTitle: "Preparing a speech or presentation?",
    ctaDescription:
      "Get help with speech structure, examples, editing, and presentation assignments from SubjectBuddy.",
    ctaButtonText: "Get Presentation Help",
    ctaButtonHref: "/services/speech-writing-help",
    faqItems: [
      {
        question: "How many words are in a 5-minute speech?",
        answer:
          "A 5-minute speech is about 650 words at an average speed of 130 words per minute.",
      },
      {
        question: "How many words should a 3-minute speech have?",
        answer:
          "A 3-minute speech is about 390 words at 130 words per minute, or roughly 300 to 480 words depending on pace.",
      },
      {
        question: "What is the average speaking speed?",
        answer:
          "A practical average for presentations is about 130 words per minute.",
      },
      {
        question: "How do I make my speech shorter?",
        answer:
          "Remove repeated points, shorten examples, and keep the central argument clear.",
      },
      {
        question: "How can I practice timing my speech?",
        answer:
          "Read the speech aloud with a timer, include pauses, and rehearse more than once before final editing.",
      },
    ],
  },
};

export default StudentUtilityToolPage;
